<template>
  <div>
    <!-- Botão de Reconvocar (oculto quando autoOpen=true) -->
    <button 
      v-if="!autoOpen"
      @click="openRecallModal" 
      class="inline-flex items-center px-3 py-1 text-sm font-medium text-purple-700 bg-purple-100 hover:bg-purple-200 dark:bg-purple-900 dark:text-purple-200 dark:hover:bg-purple-800 rounded-md transition-colors"
      :class="{ 'opacity-50 cursor-not-allowed': disabled }"
      :disabled="disabled"
      :title="disabled ? 'Botão desabilitado' : 'Reconvocar paciente'"
    >
      <ClipboardList class="w-4 h-4 mr-1" />
      Solicitar Complemento
      <span v-if="disabled" class="ml-1 text-xs">(Desabilitado)</span>
    </button>

    <!-- Modal de Reconvocação -->
    <Teleport to="body">
      <div v-if="isModalOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999999] p-4" style="position: fixed !important; z-index: 9999999 !important;">
        <div class="bg-white dark:bg-gray-800 rounded-lg max-w-lg w-full" @click.stop style="position: relative; z-index: 10000000;">
          <div class="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Solicitar Complemento</h3>
            <button @click="closeModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <X class="w-5 h-5" />
            </button>
          </div>
          
          <div class="p-6">
            <div class="mb-4">
              <p class="text-gray-600 dark:text-gray-400 mb-2">Paciente: <span class="font-medium">{{ getPatientName() }}</span></p>
              <p v-if="appointment?.accession_number" class="text-gray-600 dark:text-gray-400 mb-2">OS Anterior: <span class="font-medium text-blue-600 dark:text-blue-400">{{ appointment.accession_number }}</span></p>
              <p class="text-gray-600 dark:text-gray-400 mb-2">Exame: <span class="font-medium">{{ getServiceName() }}</span></p>
              <p class="text-gray-600 dark:text-gray-400">Data Original: <span class="font-medium">{{ formatDate(appointment?.date) }} às {{ appointment?.time?.slice(0,5) || 'Horário não informado' }}</span></p>
            </div>
            
            <!-- Aviso sobre ação -->
            <div class="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4 mb-4">
              <div class="flex items-start">
                <div class="flex-shrink-0">
                  <Info class="h-5 w-5 text-purple-400" />
                </div>
                <div class="ml-3">
                  <h3 class="text-sm font-medium text-purple-800 dark:text-purple-200">Solicitação de complemento</h3>
                  <div class="mt-2 text-sm text-purple-700 dark:text-purple-300">
                    <p>Esta solicitação será adicionada à fila de ações pendentes. Um administrativo irá processar a solicitação posteriormente.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Tipo <span class="text-red-500">*</span>
              </label>
              <select 
                v-model="recallData.type" 
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                :class="{ 'border-red-500': !recallData.type && isFormTouched }"
                required
              >
                <option value="">Selecione o tipo</option>
                <option value="pendencia">Pendência</option>
                <option value="reconvocacao">Reconvocação</option>
              </select>
              <p v-if="!recallData.type && isFormTouched" class="mt-1 text-sm text-red-600">Tipo é obrigatório</p>
            </div>
            
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Motivo <span class="text-red-500">*</span>
              </label>
              <select 
                v-model="recallData.reason" 
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                :class="{ 'border-red-500': !recallData.reason && isFormTouched }"
                required
                :disabled="!recallData.type"
              >
                <option value="">Selecione um motivo</option>
                <!-- Motivos para Pendência -->
                <optgroup v-if="recallData.type === 'pendencia'" label="Motivos de Pendência">
                  <option value="aguardando_documentos">Aguardando Documentos</option>
                  <option value="aguardando_autorizacao">Aguardando Autorização</option>
                  <option value="aguardando_pagamento">Aguardando Pagamento</option>
                  <option value="aguardando_laudo_anterior">Aguardando Laudo Anterior</option>
                  <option value="aguardando_exame_complementar">Aguardando Exame Complementar</option>
                  <option value="aguardando_avaliacao_medica">Aguardando Avaliação Médica</option>
                  <option value="outro_pendencia">Outro</option>
                </optgroup>
                <!-- Motivos para Reconvocação -->
                <optgroup v-if="recallData.type === 'reconvocacao'" label="Motivos de Reconvocação">
                  <option value="complemento_exame">Complemento de Exame</option>
                  <option value="exame_inconclusivo">Exame Inconclusivo</option>
                  <option value="nova_avaliacao">Nova Avaliação</option>
                  <option value="controle">Controle</option>
                  <option value="repeticao_exame">Repetição de Exame</option>
                  <option value="exame_insatisfatorio">Exame Insatisfatório</option>
                  <option value="outro_reconvocacao">Outro</option>
                </optgroup>
              </select>
              <p v-if="!recallData.reason && isFormTouched" class="mt-1 text-sm text-red-600">Motivo é obrigatório</p>
            </div>
            
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Descreva <span class="text-red-500">*</span>
              </label>
              <textarea 
                v-model="recallData.description" 
                rows="3" 
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                :class="{ 'border-red-500': !recallData.description && isFormTouched }"
                placeholder="Descreva detalhadamente o motivo da reconvocação..."
                required
              ></textarea>
              <p v-if="!recallData.description && isFormTouched" class="mt-1 text-sm text-red-600">Descrição é obrigatória</p>
            </div>
            
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Observações Adicionais</label>
              <textarea 
                v-model="recallData.additional_notes" 
                rows="2" 
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                placeholder="Observações adicionais (opcional)..."
              ></textarea>
            </div>
          </div>
          
          <div class="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
            <button 
              type="button" 
              @click="closeModal" 
              class="px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-white rounded-lg text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-500"
            >
              Cancelar
            </button>
            <button 
              type="button" 
              @click="handleRecallPatient" 
              :disabled="isProcessing || !isFormValid" 
              class="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <template v-if="isProcessing">
                <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Processando...</span>
              </template>
              <template v-else>
                <ClipboardList class="w-4 h-4" />
                <span>{{ isFormValid ? 'Solicitar Ação' : 'Preencha os campos obrigatórios' }}</span>
              </template>
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- BannerMessage para notificações -->
    <BannerMessage
      v-if="showBannerMessage"
      :type="bannerType"
      :message="bannerMessage"
      @close="showBannerMessage = false"
      style="z-index: 999999;"
    />
  </div>
</template>

<script>
import { ref, computed, watch, nextTick } from 'vue'
import { ClipboardList, X, Info } from 'lucide-vue-next'
import BannerMessage from '@/components/BannerMessage.vue'

export default {
  name: 'RecallPatientButton',
  components: {
    ClipboardList,
    X,
    Info,
    BannerMessage
  },
  props: {
    appointment: {
      type: Object,
      required: true
    },
    availableTimes: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    autoOpen: {
      type: Boolean,
      default: false
    }
  },
  emits: ['recall-patient'],
  setup(props, { emit }) {
    const isModalOpen = ref(false)
    const isProcessing = ref(false)
    const isFormTouched = ref(false)
    
    // Estados para BannerMessage
    const showBannerMessage = ref(false)
    const bannerMessage = ref('')
    const bannerType = ref('error')
    
    const recallData = ref({
      type: '',
      reason: '',
      description: '',
      additional_notes: ''
    })
    
    // Função para exibir banners
    const showBanner = (type, message) => {
      bannerType.value = type;
      bannerMessage.value = message;
      showBannerMessage.value = true;
    };
    
    // Watcher para monitorar mudanças na prop disabled
    watch(() => props.disabled, () => {
      // Log removido
    });
    
    // Watcher para resetar o motivo quando o tipo mudar
    watch(() => recallData.value.type, () => {
      recallData.value.reason = ''
    });
    
    // Watcher para abrir o modal automaticamente quando autoOpen for true e appointment estiver disponível
    watch([() => props.autoOpen, () => props.appointment], ([autoOpen, appointment]) => {
      if (autoOpen && appointment && !isModalOpen.value) {
        console.log('RecallPatientButton - Dados do appointment recebidos:', appointment);
        nextTick(() => {
          // Verificar novamente se o modal não está aberto antes de abrir
          if (!isModalOpen.value) {
            openRecallModal()
          }
        })
      }
    }, { immediate: true })

    const isFormValid = computed(() => {
      return recallData.value.type && recallData.value.reason && recallData.value.description
    })

    const formatDate = (date) => {
      if (!date) return ''
      return new Date(date).toLocaleDateString('pt-BR')
    }

    const getPatientName = () => {
      if (!props.appointment) return 'Paciente não informado'
      return props.appointment.patient?.name || props.appointment.patient_name || 'Paciente não informado'
    }

    const getServiceName = () => {
      if (!props.appointment) return 'Exame não informado'
      return props.appointment.service?.descricao || props.appointment.service_name || 'Exame não informado'
    }

    const openRecallModal = () => {
      // Evitar abrir o modal se já estiver aberto
      if (isModalOpen.value) {
        return
      }
      
      console.log('RecallPatientButton - Abrindo modal com dados:', props.appointment);
      recallData.value = {
        type: '',
        reason: '',
        description: '',
        additional_notes: ''
      }
      isFormTouched.value = false
      isModalOpen.value = true
    }

    const closeModal = () => {
      isModalOpen.value = false
      recallData.value = {
        type: '',
        reason: '',
        description: '',
        additional_notes: ''
      }
      isFormTouched.value = false
      isProcessing.value = false
    }

    const handleRecallPatient = async () => {
      // Marcar o formulário como tocado para mostrar erros
      isFormTouched.value = true
      
      if (!isFormValid.value) return

      // Evitar processar se já estiver processando
      if (isProcessing.value) return

      isProcessing.value = true

      try {
        // Obter o texto do motivo selecionado
        const reasonText = getReasonText(recallData.value.reason)
        const originalDate = formatDate(props.appointment.date)
        const originalTime = props.appointment.time?.slice(0, 5)
        const originalOS = props.appointment.accession_number || 'N/A'
        const patientName = getPatientName()
        const serviceName = getServiceName()
        
        // Montar o motivo completo da reconvocação
        const fullReason = `${reasonText}: ${recallData.value.description}${recallData.value.additional_notes ? ` - ${recallData.value.additional_notes}` : ''}`

        // Emite o evento com os dados da reconvocação para a fila
        emit('recall-patient', {
          appointment: props.appointment,
          reason: fullReason,
          additional_notes: recallData.value.additional_notes,
          type: recallData.value.type,
          reason_type: recallData.value.reason,
          description: recallData.value.description,
          // Incluindo os dados adicionais para uso no backend
          original_date: originalDate,
          original_time: originalTime,
          original_os: originalOS,
          patient_name: patientName,
          service_name: serviceName
        })

        showBanner('success', 'Solicitação de complemento enviada com sucesso!')
        closeModal()
      } catch (error) {
        console.error('Erro ao reconvocar paciente:', error)
        showBanner('error', 'Erro ao enviar solicitação. Tente novamente.')
      } finally {
        isProcessing.value = false
      }
    }

    const getReasonText = (reasonValue) => {
      const reasonMap = {
        // Motivos de Pendência
        'aguardando_documentos': 'Aguardando Documentos',
        'aguardando_autorizacao': 'Aguardando Autorização',
        'aguardando_pagamento': 'Aguardando Pagamento',
        'aguardando_laudo_anterior': 'Aguardando Laudo Anterior',
        'aguardando_exame_complementar': 'Aguardando Exame Complementar',
        'aguardando_avaliacao_medica': 'Aguardando Avaliação Médica',
        'outro_pendencia': 'Outro',
        // Motivos de Reconvocação
        'complemento_exame': 'Complemento de Exame',
        'exame_inconclusivo': 'Exame Inconclusivo',
        'nova_avaliacao': 'Nova Avaliação',
        'controle': 'Controle',
        'repeticao_exame': 'Repetição de Exame',
        'exame_insatisfatorio': 'Exame Insatisfatório',
        'outro_reconvocacao': 'Outro'
      }
      return reasonMap[reasonValue] || reasonValue
    }

    return {
      isModalOpen,
      isProcessing,
      isFormTouched,
      recallData,
      showBannerMessage,
      bannerMessage,
      bannerType,
      getPatientName,
      getServiceName,
      isFormValid,
      formatDate,
      openRecallModal,
      closeModal,
      handleRecallPatient,
      getReasonText
    }
  }
}
</script> 